import { useWatch } from 'antd/es/form/Form';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePaymentMethodQuery, usePaymentTermsUpdateMutation } from 'api';
import { Form, FormProps, notification } from 'components';
import { IPaymentTermsEditModal } from 'feature/clientTabs/payments/paymentTerms/edit/index';
import { ErrorMappingValueType } from 'typings';
import { PaymentTermsFieldType } from 'typings/form/client/payment';

export const usePaymentTermsEditModal = (props: IPaymentTermsEditModal) => {
  const { modalState, dataToEdit, onModalStateChange } = props;
  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const paymentType = useWatch('paymentType', form); // Watch paymentType value

  const [notificationApi, contextHolder] = notification.useNotification();
  const { data: paymentMethodData = [] } = usePaymentMethodQuery();
  const [updatePaymentTerms, { isLoading }] = usePaymentTermsUpdateMutation();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handlePrepaymentChange = useCallback(
    (value: number | null) => {
      if (value) {
        form.setFieldsValue({ postPayment: Math.max(0, 100 - value).toFixed(2) });
      } else {
        form.setFieldsValue({ postPayment: '' });
      }
    },
    [form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<PaymentTermsFieldType>['onFinish'] = useCallback(
    (values: PaymentTermsFieldType) => {
      const payload: PaymentTermsFieldType = {
        threshold: -1,
        prepayment: -1,
        postPayment: -1,
        netDaysAfterDelivery: -1,
        ...values, // Override with form values
      };
      payload.id = id;
      updatePaymentTerms(payload)
        .then(({ data, error }: any) => {
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: 'Payment Terms was successfully updated.',
            });
            onCancel();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [id, updatePaymentTerms, openNotificationWithIcon, onCancel]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue({
        ...dataToEdit,
        paymentType: dataToEdit.paymentType || 'PREPAID',
      });
    }
  }, [modalState, dataToEdit, form]);

  return {
    onFinish,
    onCancel,
    form,
    contextHolder,
    paymentMethodData,
    paymentType,
    handlePrepaymentChange,
    isLoading,
  };
};
